import axios from "axios";
import { FILTER_AI_API_URL } from "../../../config/constants";
import { attachOttoAuthRetry } from "./authRetry";

// Dedicated client for the fca-filter-ai backend (the AI "brain" + resolver).
// withCredentials so the session cookie flows on *.fcatracker.com, same as the other backends.
const filterAiApi = axios.create({
    baseURL: FILTER_AI_API_URL,
    timeout: 120000,
    withCredentials: true
});

// Dev-loop resilience. The first POST after the backend restarts (uvicorn --reload) can land on a stale
// keep-alive connection and arrive body-less, so the fresh worker 422s "body required" — the user then has
// to refresh the page. We always send a complete body, so a body-LEVEL 422 (the whole body, or a top-level
// required field, missing) or a no-response network error means the request never really landed; retry it
// ONCE on a fresh connection instead. Field-level 422s (a genuine bad value deeper in the payload) are NOT
// retried — those are real validation failures the caller should see.
const isMissingBodyField = d => {
    const leaf = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : null;
    return leaf === "body" || leaf === "utterance" || leaf === "project_id";
};

const isTransportGlitch = err => {
    // A timeout is NOT a glitch: ECONNABORTED means the request already spent the full 120s budget —
    // retrying doubles it against a backend that didn't answer, and the work may well have run.
    if (err.code === "ECONNABORTED") return false;
    if (!err.response) return true; // network error / connection reset — request didn't reach the worker
    if (err.response.status !== 422) return false;
    const detail = err.response.data && err.response.data.detail;
    return Array.isArray(detail) && detail.some(isMissingBodyField);
};

// /feedback is the one POST with no request_id dedupe server-side: a retry whose first attempt actually
// landed (the ack was what got lost) would double-record the vote. Every other POST replays safely —
// interpret/resolve dedupe by request_id, the catalog reads are idempotent.
const isRetriablePath = url => !String(url || "").endsWith("/feedback");

filterAiApi.interceptors.response.use(null, async err => {
    const cfg = err.config;
    if (cfg && !cfg._retried && cfg.method === "post" && isRetriablePath(cfg.url) && isTransportGlitch(err)) {
        cfg._retried = true;
        return filterAiApi(cfg);
    }
    return Promise.reject(err);
});

// Registered AFTER the transport-glitch retry above (interceptors run in registration order), so a
// dropped connection is still replayed as a glitch rather than reaching the auth path. This is what makes
// an expired token recoverable for Otto: without it, the filter-ai backend's 401 (its auth gate went on
// after the 2026-08-31 pen-test report) surfaces as "something went wrong" in the chat until the user
// reloads, while every other feature in the app silently refreshes and carries on.
attachOttoAuthRetry(filterAiApi);

// GET /schema — filter taxonomy (dimensions + backend keys, wildcard fields, scalar toggles, operators).
export const getFilterSchema = () => filterAiApi.get("/schema").then(res => res.data);

// POST /filter_catalog — EVERY dimension's option values for the project(s) in ONE call, served from the
// dashboard fact views. Replaces the per-field /get_list fan-out (catalog.js indexes the result by
// get_list_field). POST (not GET) so a large `buildingIds` list (D2 per-user scope) and the structured
// `activeFilters` object (D3 cascade) aren't crammed into a query string.
//   buildingIds  — the user's visible building UUIDs (permission scope). Omit for full project vocab.
//   activeFilters — { dimensions: { <get_list_field>: [ids] }, year: { start, end } } to cascade. Otto's
//                   resolve omits this (full vocabulary) to avoid candidate starvation.
export const getFilterCatalog = ({ projectId, projectIds, buildingIds, activeFilters } = {}) => {
    const ids = projectIds && projectIds.length ? projectIds.map(String) : projectId != null ? [String(projectId)] : [];
    const body = { project_ids: ids };
    if (buildingIds && buildingIds.length) body.building_ids = buildingIds.map(String);
    if (activeFilters) body.active_filters = activeFilters;
    return filterAiApi.post("/filter_catalog", body).then(res => res.data);
};

// POST /interpret — utterance -> FilterIntent (committed filters + ambiguous_terms + year range + clears).
const postInterpret = payload => filterAiApi.post("/interpret", payload).then(res => res.data);

// ---- Streamed interpret (SSE) ---------------------------------------------------------------------
// POST /interpret/stream is the SAME dispatch as /interpret, delivered as Server-Sent Events so the chat
// can paint before the turn finishes: `meta` (ids) -> `route` (the classifier's decision) -> `delta`* (prose
// as it generates — today only the platform_qa answer) -> `final` (the identical InterpretResponse).
//
// The stream is DISPLAY-ONLY: `final` is the contract and every downstream step (resolve, grounding, the
// proposal card) consumes it exactly as it consumes the plain POST's body. That is what makes falling back
// safe — see `interpretUtterance` at the bottom of this block, which owns the transport choice.
//
// axios can't do this (it's XHR-based and has no streaming response body), hence raw fetch. `credentials:
// "include"` mirrors the axios client's withCredentials so the session cookie still flows.

// PARITY-TABLE (SSE frame vocabulary) — mirrored in filter-ai/app/api/streaming.py `SSE_EVENTS`. Order and
// spelling must match: drift doesn't break loudly, it makes this parser stop recognising the frame and fall
// back to the plain POST forever, invisibly. Guarded by otto-sync's `xrepo-sse-events` parity check. The
// parser below dispatches through these bindings so the declaration can't drift from the behavior.
export const SSE_EVENTS = ["meta", "route", "delta", "final", "error"];
const [EV_META, EV_ROUTE, EV_DELTA, EV_FINAL, EV_ERROR] = SSE_EVENTS;

// A stream is only attempted where the platform can actually read a response body incrementally. jsdom
// (jest) and any older browser fall through to the plain POST with no behavior change.
export const streamingSupported = () =>
    typeof fetch === "function" &&
    typeof AbortController === "function" &&
    typeof TextDecoder === "function" &&
    typeof ReadableStream === "function";

// Thrown for an `error` FRAME: the turn itself ran and failed server-side (a 502 from the interpreter), so
// retrying over the plain POST would just pay for the same failure twice. Transport problems are not marked
// and DO fall back.
const streamTurnError = data => {
    const err = new Error((data && data.detail) || "interpretation_failed");
    err.ottoTurnFailed = true;
    err.status = (data && data.status) || 502;
    return err;
};

// Split an SSE buffer into complete frames, returning [frames, remainder]. Frames are separated by a blank
// line; the trailing piece is held back because it may be a partially-received frame.
const splitFrames = buffer => {
    const parts = buffer.split(/\r?\n\r?\n/);
    return [parts.slice(0, -1), parts[parts.length - 1]];
};

const parseFrame = frame => {
    let event = "message";
    const data = [];
    frame.split(/\r?\n/).forEach(line => {
        if (!line || line.startsWith(":")) return; // "" or a `: ping` heartbeat comment
        const at = line.indexOf(":");
        const field = at === -1 ? line : line.slice(0, at);
        const value = at === -1 ? "" : line.slice(at + 1).replace(/^ /, "");
        if (field === "event") event = value;
        else if (field === "data") data.push(value);
    });
    if (!data.length) return null;
    try {
        return { event, data: JSON.parse(data.join("\n")) };
    } catch (e) {
        return null; // a frame we can't parse is a display hint we drop, never a turn failure
    }
};

// stallMs — abort when the connection goes quiet for this long. The server heartbeats every 15s precisely so
// a legitimately slow turn (a filter_query has no prose to stream) can't be mistaken for a dead connection.
// totalMs — a hard ceiling matching the axios client's timeout, so a backend that heartbeats forever while
// wedged still releases the UI.
export const interpretUtteranceStream = async (
    payload,
    { onMeta, onRoute, onDelta, stallMs = 30000, totalMs = 120000 } = {}
) => {
    const controller = new AbortController();
    let stallTimer = null;
    const armStall = () => {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = setTimeout(() => controller.abort(), stallMs);
    };
    const totalTimer = setTimeout(() => controller.abort(), totalMs);
    let reader = null;

    try {
        armStall();
        const res = await fetch(`${FILTER_AI_API_URL}/interpret/stream`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
            body: JSON.stringify(payload),
            signal: controller.signal
        });
        // Includes 404 (backend predates streaming) and 503 (streaming_disabled / rollout) — all "use the
        // plain POST", none of them a turn failure.
        if (!res.ok || !res.body) throw new Error(`stream_unavailable_${res.status}`);

        reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let final = null;

        for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            armStall();
            buffer += decoder.decode(value, { stream: true });
            const [frames, rest] = splitFrames(buffer);
            buffer = rest;
            for (const raw of frames) {
                const frame = parseFrame(raw);
                if (!frame) continue;
                if (frame.event === EV_META && onMeta) onMeta(frame.data);
                else if (frame.event === EV_ROUTE && onRoute) onRoute(frame.data.intent_kind);
                else if (frame.event === EV_DELTA && onDelta) onDelta(frame.data.text || "");
                else if (frame.event === EV_ERROR) throw streamTurnError(frame.data);
                else if (frame.event === EV_FINAL) final = frame.data;
            }
        }
        // A stream that ends without `final` never delivered the turn — transport-shaped, so retryable.
        if (!final) throw new Error("stream_incomplete");
        return final;
    } finally {
        if (stallTimer) clearTimeout(stallTimer);
        clearTimeout(totalTimer);
        // Release the connection when we bail out mid-body (an error frame, a stall abort) rather than
        // leaving it open until GC. A no-op once the body has been fully read. Fully guarded: anything
        // thrown from this cleanup would REPLACE an already-successful `final` and send the caller down
        // the fallback path for a turn that actually worked.
        try {
            if (reader && reader.cancel) reader.cancel().catch(() => {});
        } catch (e) {
            /* nothing to do — the request is over either way */
        }
    }
};

// Interpret one utterance. ONE function, two transports: given progress handlers (the live chat) it rides
// the SSE endpoint and reports progress as the turn happens; given none — or when the platform can't stream,
// or the stream fails for any transport reason — it is the plain POST, with the SAME payload and therefore
// the same request_id, which the backend dedupes turns by. So a fallback can never double-persist the turn
// or duplicate its trace, and callers get the identical response object either way.
//
// `onReset` fires just before a fallback, so a caller that already painted partial text can clear it rather
// than leave a truncated answer on screen.
export const interpretUtterance = async (payload, handlers = null) => {
    const { onReset, ...rest } = handlers || {};
    const wantsStream = !!(rest.onDelta || rest.onRoute || rest.onMeta);
    if (!wantsStream || !streamingSupported()) return postInterpret(payload);
    try {
        return await interpretUtteranceStream(payload, rest);
    } catch (e) {
        // An `error` frame means the turn itself failed server-side; surface it exactly as the plain POST's
        // rejection would rather than paying for the same failed dispatch a second time.
        if (e && e.ottoTurnFailed) throw e;
        if (onReset) onReset();
        return postInterpret(payload);
    }
};

// POST /resolve — phrase(s) + per-field candidates -> per-field decision (auto/confirm/clarify) + suggestions.
export const resolvePhrases = payload => filterAiApi.post("/resolve", payload).then(res => res.data);

// GET /config — whether the assistant is enabled for this user (kill switch + gradual rollout).
export const getAssistantConfig = userId =>
    filterAiApi.get("/config", { params: { user_id: userId || undefined } }).then(res => res.data);

// POST /feedback — thumbs up/down on an interpretation, tied to the turn via request_id.
export const sendFeedback = payload => filterAiApi.post("/feedback", payload).then(res => res.data);

// GET /sessions — the logged-in user's recent conversations (most recent first) for the history panel.
// Scoped by user_id + project_id (matching how turns are persisted); `limit`/`offset` drive "last 10 +
// load more". Each session carries a `preview` (first utterance) + `turn_count` for the list item.
export const listSessions = ({ userId, projectId, limit = 10, offset = 0 } = {}) =>
    filterAiApi
        .get("/sessions", {
            params: {
                user_id: userId || undefined,
                project_id: projectId || undefined,
                limit,
                offset
            }
        })
        .then(res => res.data);

// GET /sessions/{id} — one conversation's turns (utterance + intent), used to resume it in the chat.
export const getSession = sessionId => filterAiApi.get(`/sessions/${sessionId}`).then(res => res.data);

// POST /turns/display — report the card text Otto actually RENDERED for a turn (grounded headline,
// lead-ins, offered options), so resuming from history replays what the user SAW — the persisted intent
// only carries the model's pre-resolve draft. Fire-and-forget side channel: a failure just means this
// turn falls back to the deterministic history summary.
export const reportDisplayedText = ({ requestId, message }) =>
    filterAiApi.post("/turns/display", { request_id: requestId, message }).then(res => res.data);

// GET /otto_status — the backend's own dependency state (Postgres, OpenAI key) plus which Otto it is
// (otto_version + build), for the startup connectivity report.
//
// NOT /health, deliberately. Deployed, Otto is same-origin and the frontend nginx answers `/health` itself
// with a static "healthy" string for the frontend pod's liveness probe — it never reaches filter-ai. That
// silently made this call return a STRING, and since a string has no `.database`, the report used to
// announce "database UNREACHABLE" in every deployed environment while Otto was fine. `/health` can't simply
// be proxied here either: that would couple frontend liveness to Otto's Postgres. Hence a distinct name
// that collides with nothing.
//
// Allowlisted in the frontend chart's nginx since (verified live on dev3, 2026-08-06 — every path in
// filter-ai/deploy/frontend-proxy-paths.json answers as FastAPI JSON). If a deployed env ever regresses,
// it falls through to the SPA and returns index.html, which startupDiagnostics reports honestly instead
// of mistaking for a dependency failure; the manifest is the runbook for fixing the allowlist.
export const filterAiStatus = () => filterAiApi.get("/otto_status").then(res => res.data);

export default filterAiApi;
